import { useCallback, useEffect, useState } from 'react';
import api from '@/api';
import { pcommon, ptax } from '@/proto/proto';
import {
  API_TAX_CHANGE_REQUEST_HISTORY,
  FORMAT_DATE_GENERAL_EXCEPT_SECOND,
  FORMAT_DATE_GENERAL_EXCEPT_TIME,
  IMAGE_PATH_TAX_NOTICE,
} from '@/constants/constant';

import { useSelector } from 'react-redux';
import { RootState } from '@/rootReducer';
import Title from '@/components/atoms/text/Title';
import TaxTemplate from '@/components/template/tax/TaxTemplate';
import TaxChangeHistoryListItem from '@/components/organisms/tax/change/history/TaxChangeHistoryListItem';
import TaxChangeHistoryListFilter from '@/components/organisms/tax/change/history/TaxChangeHistoryListFilter';
import queryString from 'query-string';
import { useLocation } from 'react-router';
import { writeFile } from '@/utils/excelExtension';
import moment from 'moment';
import { List, message, Modal } from 'antd';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import TaxChangeInformationModal from '@/components/organisms/tax/change/TaxChangeInformationModal';
import TaxChangeType, { nameOf } from '@/components/covalent/tax/change/TaxChangeType';
import TaxChangeStatus from '@/components/covalent/tax/change/TaxChangeStatus';
import TaxChangeRequestModal from '@/components/organisms/tax/change/TaxChangeRequestModal';
import TaxChangeRequestMethod, { apiOf } from '@/components/covalent/tax/change/TaxChangeRequestMethod';
import { makePdfToFile } from '@/utils/fileExtension';
import { ErrorMessage } from '@/constants/message';
import { FilterDateRange } from '@/components/organisms/tax/notice/list/TaxNoticeListFilter';

type InformationModalStateType = {
  visible: boolean;
  data?: ptax.IPbTaxChangeRequest;
};

type ResendModalStateType = {
  taxNotices?: ptax.IPbTaxNotice[];
  visible: boolean;
};

export type FilterStateType = pcommon.IPbPageable & {
  keyword?: string;
  date?: number;
  type?: TaxChangeType;
  status?: TaxChangeStatus;
  from?: string;
  to?: string;
};

const TaxChangeHistoryPage = () => {
  const { search } = useLocation();
  const { current, total, page, size, q, date, from, to, status, type } = queryString.parse(search);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ptax.IPbTaxChangeRequestList>();
  const { current: company } = useSelector((state: RootState) => state.company);
  const [isDownloadable, setDownloadable] = useState(false);
  const [keyword, setKeyword] = useState<any>();

  const [preview, setPreview] = useState({
    visible: false,
    image: '',
  });

  const [informationState, setInformationState] = useState<InformationModalStateType>({
    visible: false,
  });

  const [resendState, setResendState] = useState<ResendModalStateType>({
    visible: false,
  });

  const [filter, setFilter] = useState<FilterStateType>({
    current: Number(current ?? 1),
    page: Number(page ?? 0),
    size: Number(size ?? 10),
    date: Number(date ?? FilterDateRange.WHOLE),
    type: Number(type ?? TaxChangeType.ALL),
    status: Number(status ?? TaxChangeStatus.ALL),
    keyword: String(q ?? ''),
  });
  const getChangeRequests = useCallback(
    (companyId) => {
      setLoading(true);
      api
        .get(API_TAX_CHANGE_REQUEST_HISTORY, { params: { ...filter, companyId } })
        .then((response) => {
          setData(response.data);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [filter],
  );

  const fetch = useCallback(() => {
    if (company && company.base) {
      getChangeRequests(company.base.id);
    }
  }, [company, getChangeRequests]);

  const download = useCallback(() => {
    setLoading(true);
    api
      .get(API_TAX_CHANGE_REQUEST_HISTORY, { params: { ...filter, size: 10000, companyId: company.base?.id } })
      .then((response) => {
        writeFile(
          response.data?.request?.map((request: ptax.IPbTaxChangeRequest) => ({
            createAt: moment(request?.base?.createdAt).format(FORMAT_DATE_GENERAL_EXCEPT_SECOND),
            method: nameOf(request?.typeCode ?? 0),
            sender: request.taxNotice?.sender,
            plateNo: request?.taxNotice?.plateNo,
            violationAt: moment(request.taxNotice?.violation?.first()?.violationAt).format(FORMAT_DATE_GENERAL_EXCEPT_SECOND),
            location: request.taxNotice?.violation?.first()?.location,
            details: request.taxNotice?.violation?.first()?.details,
          })) ?? [],
          ['등록일시', '등록방식', '발급기관', '차량번호', '위반일시', '위반장소', '위반내용'],
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [company, filter]);

  const resend = useCallback(
    async (taxNoticeList: ptax.IPbTaxNotice[], method: TaxChangeRequestMethod, contactId?: number) => {
      setLoading(true);

      const { id } = company.base;

      const formData = new FormData();

      formData.append('companyId', id);

      if (contactId) {
        formData.append('contactId', `${contactId}`);
      }

      await taxNoticeList.map(async (item, index) => {
        formData.append('taxNoticeIdList', `${item.base?.id}`);
        await makePdfToFile(item, company, `document${index}.pdf`).then((pdf) => {
          formData.append('files', pdf);
        });
      });

      api
        .upload(apiOf(method), formData)
        .then(() => {
          message.success(`${taxNoticeList.length}건의 민원을 처리했습니다.`);
          fetch();
        })
        .catch((e) => {
          const error = e?.response?.data;
          if (error) {
            message.error(error.message);
          } else {
            message.error(ErrorMessage.network.disconnected);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [company, fetch],
  );

  useEffect(() => {
    fetch();
  }, [fetch, filter]);

  useEffect(() => {
    const isDataExist = (data?.request?.length ?? 0) > 0;
    const isDateRangeExist = (filter.from?.length ?? 0) > 0 && (filter.to?.length ?? 0) > 0;
    const diffMonths = moment(filter.from, FORMAT_DATE_GENERAL_EXCEPT_TIME).diff(moment(filter.to, FORMAT_DATE_GENERAL_EXCEPT_TIME), 'months');
    const isDateRangeIn = diffMonths <= 3;
    setDownloadable(isDataExist && isDateRangeExist && isDateRangeIn);
  }, [data, filter.from, filter.to]);

  return (
    <TaxTemplate>
      <Title>민원 관리</Title>
      <TaxChangeHistoryListFilter
        filter={filter}
        downloadable={isDownloadable}
        onSearch={(searchKeyword) => {
          setFilter({ ...filter, keyword: searchKeyword.replace(' ', '+') });
        }}
        onChangeSearch={(searchKeyword) => setKeyword(searchKeyword)}
        onClickDownload={download}
        onChangeType={(value) => setFilter({ ...filter, type: value })}
        onChangeStatus={(value) => setFilter({ ...filter, status: value })}
        onChangeDateRange={(first, second) => {
          setFilter({ ...filter, from: first, to: second });
        }}
      />
      <List
        loading={loading}
        dataSource={data?.request ?? []}
        itemLayout="vertical"
        pagination={{
          current: Number(data?.pageable?.current ?? 0) + 1,
          total: Number(data?.pageable?.total ?? 1),
          // eslint-disable-next-line no-shadow
          onChange: (page, size) => {
            setFilter({ ...filter, page: page - 1, size });
          },
          className: 'pagination',
          size: 'small',
          position: 'bottom',
        }}
        renderItem={(item: ptax.IPbTaxChangeRequest) => {
          return (
            <TaxChangeHistoryListItem
              data={item}
              company={company}
              keyword={keyword}
              onClickImage={() =>
                setPreview({
                  visible: true,
                  image: IMAGE_PATH_TAX_NOTICE(Number(company.base?.id ?? -1), item.taxNotice),
                })
              }
              onClickInformation={() =>
                setInformationState({
                  visible: true,
                  data: item,
                })
              }
              onClickResend={() => {
                if (item.taxNotice) {
                  setResendState({
                    visible: true,
                    taxNotices: [item.taxNotice],
                  });
                }
              }}
            />
          );
        }}
      />
      <Modal visible={preview.visible} onCancel={() => setPreview({ visible: false, image: '' })}>
        <LazyLoadImage src={preview.image} />
      </Modal>
      <TaxChangeInformationModal
        taxChangeRequest={informationState.data}
        company={company}
        visible={informationState.visible}
        onCancel={() => setInformationState({ ...informationState, visible: false })}
      />
      <TaxChangeRequestModal
        title="민원 재신청"
        onCancel={() => setResendState({ ...resendState, visible: false })}
        visible={resendState.visible}
        onSend={resend}
        company={company}
        taxNotices={resendState.taxNotices}
      />
    </TaxTemplate>
  );
};

export default TaxChangeHistoryPage;
